import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import WizardSelect from '../ui/WizardSelect';
import { setFlashToast } from '../../utils/flashToast';
import {
  documentMoverLabel,
  forwardUserLabel,
  listDocumentMovementOfficers,
  listEligibleForwardUsers,
} from '../../services/taskForwarding';
import { listCaseRequestSubsidiaryTasks, revertSubsidiaryTask } from '../../services/caseRequestSubsidiaryTasks';
import { listRevertStages, isAllocationOfficerRevertStage, listWorkflowTemplateStageUsersActive, revertStageLabel, revertUserTask, type RevertStageOption } from '../../services/taskRevert';

const MAX_REALLOCATION_REASON_LENGTH = 500;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

function isSubsidiaryTaskInProgress(status: string): boolean {
  const s = status.trim().toLowerCase();
  return s.includes('progress') || s.includes('started');
}

type LoadState = 'idle' | 'loading' | 'error';

type RevertTaskModalProps = {
  open: boolean;
  caseRequestId: string;
  /** workflow_instance_stage_user_task_id */
  workflowInstanceStageUserTaskId: string;
  /** CMS-AUQ-1: category_id */
  fileCategoryId: string;
  /** CMS-AUQ-1: sub_category_id */
  subFileCategoryId: string;
  /** When true, stage and user are pre-filled from header and read-only. */
  isSubsidiaryTask?: boolean;
  /** Header current stage — used to pre-select revert stage for subsidiary tasks. */
  currentStage?: string;
  /** Header task_created_by_id — revert target user for subsidiary tasks. */
  taskCreatedById?: string;
  /** Header task_created_by_name — display label for subsidiary revert user. */
  taskCreatedByName?: string;
  onClose: () => void;
  onSuccess: (revertUserId: string) => void;
};

/** Data dictionary: Intranet — Revert Task pop-up. */
export function RevertTaskModal({
  open,
  caseRequestId,
  workflowInstanceStageUserTaskId,
  fileCategoryId,
  subFileCategoryId,
  isSubsidiaryTask = false,
  currentStage = '',
  taskCreatedById = '',
  taskCreatedByName = '',
  onClose,
  onSuccess,
}: RevertTaskModalProps) {
  const { t } = useTranslation();
  const [routingRuleId, setRoutingRuleId] = useState('');
  const [revertUserId, setRevertUserId] = useState('');
  const [reallocationReason, setReallocationReason] = useState('');
  const [physicalRequired, setPhysicalRequired] = useState(true);
  const [documentMoverId, setDocumentMoverId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [subtaskConfirmOpen, setSubtaskConfirmOpen] = useState(false);

  const [stagesState, setStagesState] = useState<LoadState>('idle');
  const [stageRows, setStageRows] = useState<RevertStageOption[]>([]);
  const [stageOptions, setStageOptions] = useState<{ id: string; label: string }[]>([]);

  const [usersState, setUsersState] = useState<LoadState>('idle');
  const [userOptions, setUserOptions] = useState<{ id: string; label: string }[]>([]);

  const [moversState, setMoversState] = useState<LoadState>('idle');
  const [moverOptions, setMoverOptions] = useState<{ id: string; label: string }[]>([]);

  const [hasInProgressSubtasks, setHasInProgressSubtasks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allocationOfficerUserName, setAllocationOfficerUserName] = useState('');
  const [allocationOfficerUserState, setAllocationOfficerUserState] = useState<LoadState>('idle');
  const [subsidiaryStageDisplay, setSubsidiaryStageDisplay] = useState('');

  useEffect(() => {
    if (!open) return;

    /* eslint-disable react-hooks/set-state-in-effect */
    setRoutingRuleId('');
    setRevertUserId(isSubsidiaryTask ? taskCreatedById.trim() : '');
    setReallocationReason('');
    setPhysicalRequired(true);
    setDocumentMoverId('');
    setErrors({});
    setCancelConfirmOpen(false);
    setSubtaskConfirmOpen(false);
    setHasInProgressSubtasks(false);
    setUserOptions([]);
    setAllocationOfficerUserName('');
    setAllocationOfficerUserState('idle');
    setSubsidiaryStageDisplay(isSubsidiaryTask ? currentStage.trim() : '');
    /* eslint-enable react-hooks/set-state-in-effect */

    setStagesState(isSubsidiaryTask ? 'idle' : 'loading');
    setStageRows([]);
    setStageOptions([]);
    setUsersState(isSubsidiaryTask ? 'idle' : 'loading');
    setMoversState('loading');
    setMoverOptions([]);

    const stageController = new AbortController();
    const userController = new AbortController();
    const moverController = new AbortController();
    const subtaskController = new AbortController();

    if (!isSubsidiaryTask) {
      void listRevertStages({
        userTaskId: workflowInstanceStageUserTaskId,
        signal: stageController.signal,
      })
        .then((r) => {
          if (stageController.signal.aborted) return;
          if (!r.success) {
            setStagesState('error');
            setFlashToast({ message: r.message || 'Failed to load revert stages.', variant: 'error' });
            return;
          }
          const opts = r.data.map(revertStageLabel);
          setStageRows(r.data);
          setStageOptions(opts);
          setStagesState('idle');
          if (opts.length === 1) {
            setRoutingRuleId(opts[0].id);
          }
        })
        .catch(() => {
          if (!stageController.signal.aborted) setStagesState('error');
        });
    }

    if (!isSubsidiaryTask) {
      void listEligibleForwardUsers({
        categoryId: fileCategoryId,
        subCategoryId: subFileCategoryId,
        signal: userController.signal,
      })
        .then((r) => {
          if (userController.signal.aborted) return;
          if (!r.success) {
            setUsersState('error');
            setFlashToast({ message: r.message || 'Failed to load users.', variant: 'error' });
            return;
          }
          const opts = r.data.map(forwardUserLabel).filter((x): x is { id: string; label: string } => Boolean(x));
          setUserOptions(opts);
          setUsersState('idle');
        })
        .catch(() => {
          if (!userController.signal.aborted) setUsersState('error');
        });
    }

    void listDocumentMovementOfficers(moverController.signal)
      .then((r) => {
        if (moverController.signal.aborted) return;
        if (!r.success) {
          setMoversState('error');
          setFlashToast({ message: r.message || 'Failed to load document movers.', variant: 'error' });
          return;
        }
        const opts = r.data.map(documentMoverLabel).filter((x): x is { id: string; label: string } => Boolean(x));
        setMoverOptions(opts);
        setMoversState('idle');
      })
      .catch(() => {
        if (!moverController.signal.aborted) setMoversState('error');
      });

    if (!isSubsidiaryTask && caseRequestId.trim()) {
      void listCaseRequestSubsidiaryTasks({ caseRequestId, signal: subtaskController.signal })
        .then((r) => {
          if (subtaskController.signal.aborted) return;
          if (!r.success) return;
          const inProgress = r.data.tasks.some((t) => isSubsidiaryTaskInProgress(t.status));
          setHasInProgressSubtasks(inProgress);
        })
        .catch(() => {
          /* non-blocking */
        });
    }

    return () => {
      stageController.abort();
      userController.abort();
      moverController.abort();
      subtaskController.abort();
    };
  }, [
    open,
    caseRequestId,
    workflowInstanceStageUserTaskId,
    fileCategoryId,
    subFileCategoryId,
    isSubsidiaryTask,
    currentStage,
    taskCreatedById,
  ]);

  const selectedStage = useMemo(
    () => stageRows.find((s) => s.routingRuleId === routingRuleId) ?? null,
    [stageRows, routingRuleId],
  );
  const isAllocationOfficerStage =
    !isSubsidiaryTask && selectedStage ? isAllocationOfficerRevertStage(selectedStage.name) : false;

  useEffect(() => {
    if (!open || isSubsidiaryTask || !selectedStage || !isAllocationOfficerStage) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setAllocationOfficerUserName('');
      setAllocationOfficerUserState('idle');
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    const workflowTemplateStageId = selectedStage.workflowTemplateStageId.trim();
    if (!workflowTemplateStageId) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setAllocationOfficerUserName('');
      setRevertUserId('');
      setAllocationOfficerUserState('error');
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    const controller = new AbortController();
    setAllocationOfficerUserState('loading');
    setAllocationOfficerUserName('');
    setRevertUserId('');

    void listWorkflowTemplateStageUsersActive({
      workflowTemplateStageId,
      signal: controller.signal,
    })
      .then((r) => {
        if (controller.signal.aborted) return;
        if (!r.success || r.data.length === 0) {
          setAllocationOfficerUserName('');
          setRevertUserId('');
          setAllocationOfficerUserState('error');
          if (!r.success) {
            setFlashToast({ message: r.message || 'Failed to load allocation officer.', variant: 'error' });
          }
          return;
        }
        const first = r.data[0];
        setRevertUserId(first.id);
        setAllocationOfficerUserName(first.displayName);
        setAllocationOfficerUserState('idle');
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAllocationOfficerUserName('');
          setRevertUserId('');
          setAllocationOfficerUserState('error');
        }
      });

    return () => controller.abort();
  }, [open, isSubsidiaryTask, selectedStage, isAllocationOfficerStage]);

  useEffect(() => {
    if (physicalRequired) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setDocumentMoverId('');
    setErrors((e) => ({ ...e, documentMoverId: '' }));
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [physicalRequired]);

  const allocationOfficerDisplayName =
    allocationOfficerUserState === 'loading'
      ? t('common.loading')
      : allocationOfficerUserName.trim() || '—';

  const stagePlaceholder = stagesState === 'error' ? t('tasks.modals.revert.loadStagesFailed') : t('tasks.modals.revert.selectStage');
  const userPlaceholder = usersState === 'error' ? t('tasks.modals.revert.loadUsersFailed') : t('tasks.modals.revert.selectUser');
  const moverPlaceholder = moversState === 'error' ? t('tasks.modals.revert.loadMoversFailed') : t('tasks.modals.forward.selectDocumentMover');

  const subsidiaryUserDisplay = taskCreatedByName.trim() || '—';
  const subsidiaryStageValue = subsidiaryStageDisplay.trim() || currentStage.trim() || '—';

  const canRevert = useMemo(() => {
    if (!workflowInstanceStageUserTaskId.trim()) return false;
    if (isSubsidiaryTask) {
      if (physicalRequired && !documentMoverId.trim()) return false;
      return true;
    }
    if (!routingRuleId.trim() || !revertUserId.trim()) return false;
    if (isAllocationOfficerStage && allocationOfficerUserState === 'loading') return false;
    if (isAllocationOfficerStage && !reallocationReason.trim()) return false;
    if (physicalRequired && !documentMoverId.trim()) return false;
    return true;
  }, [
    workflowInstanceStageUserTaskId,
    routingRuleId,
    revertUserId,
    isSubsidiaryTask,
    isAllocationOfficerStage,
    allocationOfficerUserState,
    reallocationReason,
    physicalRequired,
    documentMoverId,
  ]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!workflowInstanceStageUserTaskId.trim()) next.workflowInstanceStageUserTaskId = t('tasks.modals.revert.userTaskRequired');
    if (!isSubsidiaryTask) {
      if (!routingRuleId.trim()) next.routingRuleId = t('tasks.modals.revert.stageRequired');
      if (!revertUserId.trim()) next.revertUserId = t('tasks.modals.revert.userRequired');
    }
    if (isAllocationOfficerStage && !reallocationReason.trim()) {
      next.reallocationReason = t('tasks.modals.revert.reallocationReasonRequired');
    } else if (reallocationReason.length > MAX_REALLOCATION_REASON_LENGTH) {
      next.reallocationReason = t('tasks.modals.revert.reallocationReasonTooLong', { max: MAX_REALLOCATION_REASON_LENGTH });
    }
    if (physicalRequired && !documentMoverId.trim()) next.documentMoverId = t('tasks.modals.forward.selectDocumentMoverRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const beginCancel = () => {
    if (submitting) return;
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    onClose();
  };

  const performRevert = async () => {
    setSubmitting(true);
    const res = isSubsidiaryTask
      ? await revertSubsidiaryTask({
          body: {
            workflowInstanceStageUserTaskId,
            physicalFileMovementRequired: physicalRequired,
            documentMoverId: physicalRequired ? documentMoverId : undefined,
          },
        })
      : await revertUserTask({
          workflowInstanceStageUserTaskId,
          routingRuleId,
          revertUserId,
          physicalFileMovementRequired: physicalRequired,
          documentMoverId: physicalRequired ? documentMoverId : undefined,
          reallocationReason: isAllocationOfficerStage ? reallocationReason.trim() : undefined,
        });
    setSubmitting(false);

    if (!res.success) {
      setFlashToast({ message: res.message || t('tasks.modals.revert.revertFailed'), variant: 'error' });
      return;
    }

    setFlashToast({ message: t('tasks.modals.revert.revertSuccess'), variant: 'success' });
    onSuccess(isSubsidiaryTask ? taskCreatedById.trim() : revertUserId);
  };

  const submit = async () => {
    if (submitting) return;
    if (!validate()) return;

    if (hasInProgressSubtasks) {
      setSubtaskConfirmOpen(true);
      return;
    }

    await performRevert();
  };

  const confirmRevertWithSubtasks = () => {
    setSubtaskConfirmOpen(false);
    void performRevert();
  };

  return (
    <Modal
      open={open}
      title={t('tasks.modals.revert.title')}
      onClose={() => {
        beginCancel();
      }}
    >
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('tasks.modals.forward.discardTitle')}
        message={t('tasks.modals.forward.discardMessage')}
        confirmDisabled={submitting}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />

      <ConfirmDialog
        isOpen={subtaskConfirmOpen}
        title={t('tasks.modals.revert.subtaskConfirmTitle')}
        message={t('tasks.modals.revert.subtaskConfirmMessage')}
        confirmDisabled={submitting}
        onClose={() => setSubtaskConfirmOpen(false)}
        onConfirm={confirmRevertWithSubtasks}
      />

      {hasInProgressSubtasks && !subtaskConfirmOpen ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(255, 193, 7, 0.12)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(255, 193, 7, 0.35)',
          }}
          role="status"
        >
          {t('tasks.modals.revert.subtaskWarning')}
        </div>
      ) : null}

      {errors.workflowInstanceStageUserTaskId ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 53, 69, 0.08)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(220, 53, 69, 0.18)',
          }}
          role="alert"
        >
          {errors.workflowInstanceStageUserTaskId}
        </div>
      ) : null}

      <div className="cfg-assignment-form" style={{ padding: 0 }}>
        <div className="ccr-fg">
          <label>
            {t('tasks.modals.revert.revertToStage')} <span className="req">*</span>
          </label>
          {isSubsidiaryTask ? (
            <input
              id="revert-task-stage"
              className="ccr-inp"
              type="text"
              value={subsidiaryStageValue}
              readOnly
              disabled
              aria-readonly="true"
            />
          ) : (
            <WizardSelect
              id="revert-task-stage"
              hasError={Boolean(errors.routingRuleId)}
              value={routingRuleId}
              placeholder={stagePlaceholder}
              options={stageOptions}
              disabled={stagesState === 'error' || submitting}
              loading={stagesState === 'loading'}
              emptyMessage={t('common.noResultsFound')}
              onChange={(id) => {
                setRoutingRuleId(id);
                setReallocationReason('');
                setErrors((e) => ({ ...e, routingRuleId: '', revertUserId: '', reallocationReason: '' }));
                const stage = stageRows.find((s) => s.routingRuleId === id);
                if (!stage || !isAllocationOfficerRevertStage(stage.name)) {
                  setRevertUserId('');
                }
              }}
            />
          )}
          <FieldError message={errors.routingRuleId} />
        </div>

        <div className="ccr-fg" style={{ marginTop: 14 }}>
          <label>
            {t('tasks.modals.revert.selectUser')} <span className="req">*</span>
          </label>
          {isSubsidiaryTask || isAllocationOfficerStage ? (
            <input
              id="revert-task-user"
              className="ccr-inp"
              type="text"
              value={isSubsidiaryTask ? subsidiaryUserDisplay : allocationOfficerDisplayName}
              readOnly
              disabled
              aria-readonly="true"
            />
          ) : (
            <WizardSelect
              id="revert-task-user"
              hasError={Boolean(errors.revertUserId)}
              value={revertUserId}
              placeholder={userPlaceholder}
              options={userOptions}
              disabled={usersState === 'error' || submitting}
              loading={usersState === 'loading'}
              emptyMessage={t('common.noDataFound')}
              onChange={(id) => {
                setRevertUserId(id);
                setErrors((e) => ({ ...e, revertUserId: '' }));
              }}
            />
          )}
          <FieldError message={errors.revertUserId} />
        </div>

        {isAllocationOfficerStage ? (
          <div className="ccr-fg" style={{ marginTop: 14 }}>
            <label>
              {t('tasks.modals.revert.reallocationReason')} <span className="req">*</span>
            </label>
            <textarea
              id="revert-task-reallocation-reason"
              className={`ccr-inp${errors.reallocationReason ? ' has-error' : ''}`}
              rows={4}
              placeholder={t('tasks.modals.revert.reallocationReasonPlaceholder')}
              value={reallocationReason}
              disabled={submitting}
              maxLength={MAX_REALLOCATION_REASON_LENGTH}
              onChange={(e) => {
                setReallocationReason(e.target.value);
                setErrors((prev) => ({ ...prev, reallocationReason: '' }));
              }}
            />
            <p style={{ fontSize: 11, color: 'var(--t3)', marginTop: 6, marginBottom: 0 }}>
              {reallocationReason.length}/{MAX_REALLOCATION_REASON_LENGTH}
            </p>
            <FieldError message={errors.reallocationReason} />
          </div>
        ) : null}

        <div className="ccr-fg" style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={physicalRequired}
              disabled={submitting}
              onChange={(e) => setPhysicalRequired(e.target.checked)}
            />
            {t('tasks.modals.forward.physicalFileMovement')}
          </label>
        </div>

        {physicalRequired ? (
          <div className="ccr-fg" style={{ marginTop: 12 }}>
            <label>
              {t('tasks.modals.forward.selectDocumentMover')} <span className="req">*</span>
            </label>
            <WizardSelect
              id="revert-task-mover"
              hasError={Boolean(errors.documentMoverId)}
              value={documentMoverId}
              placeholder={moverPlaceholder}
              options={moverOptions}
              disabled={moversState === 'error' || submitting}
              loading={moversState === 'loading'}
              emptyMessage={t('common.noDataFound')}
              onChange={(id) => {
                setDocumentMoverId(id);
                setErrors((e) => ({ ...e, documentMoverId: '' }));
              }}
            />
            <FieldError message={errors.documentMoverId} />
          </div>
        ) : null}
      </div>

      <div className="cn-footer cn-footer--plain">
        <button className="mfb" type="button" disabled={submitting} onClick={beginCancel}>
          {t('common.cancel')}
        </button>
        <button
          className="mfb mfb-dk"
          type="button"
          disabled={submitting || !canRevert}
          onClick={() => void submit()}
        >
          {submitting ? t('tasks.modals.revert.reverting') : t('tasks.modals.revert.revertTask')}
        </button>
      </div>
    </Modal>
  );
}
